import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import CloseButton from '@/components/UI/CloseButton/CloseButton';
import Resizer from '@/components/UI/Resizer/Resizer';
import { HELP_WIDTH } from '@/helpers/constants';
import { isSubCR } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import useError from '@/hooks/useError';
import { setCampaignHelp } from '@/redux/Campaign/campaign.action';
import { getCampaignHelp } from '@/redux/Campaign/campaign.reducer';
import { setHelp } from '@/redux/Help/help.action';
import { getHelp } from '@/redux/Help/help.reducer';
import { setResizerHelp } from '@/redux/User/user.action';
import { getHelpResizer, getUserAll } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import { HelpTextState, UserCompleteInfo } from '@/types';
import styles from './Help.module.scss';

interface HelpProps {
  tab: string;
  userData: UserCompleteInfo;
  resizerWidth: number;
  setResizer: (value: number, save?: boolean) => void;
  setCurHelp: (help: HelpTextState) => void;
  help: any;
  campaignHelp: HelpTextState;
  setHelp: (help: HelpTextState) => void;
}

const Help: React.FC<HelpProps> = (props) => {
  const { tab, userData, resizerWidth, setResizer, setCurHelp, help, campaignHelp, setHelp } = props;
  const inSubCR = isSubCR();
  const area = () => {
    if (tab === 'plant-feedback') return inSubCR ? help.summary_plant_feedback : help.summary_feedback;

    return help?.focus;
  };
  const { t } = useTranslation();
  const [width, setWidth] = useState(resizerWidth);
  const { handleCriticalError } = useError();
  const currentLangUpper = useAppLanguage();
  const helpTab = {
    description: help?.description?.DESCRIPTION_TAB?.[currentLangUpper],
    attachments: help?.attachments?.ATTACHMENTS_TAB?.[currentLangUpper],
    classification: help?.classification?.CLASSIFICATION_TAB?.[currentLangUpper],
    affected_hardware: help?.affectedHardware?.AFFECTED_HARDWARE_TAB?.[currentLangUpper],
    affected_software: help?.affectedSoftware?.AFFECTED_SOFTWARE_TAB?.[currentLangUpper],
    affected_vehicles: help?.affectedVehicles?.AFFECTED_VEHICLES_TAB?.[currentLangUpper],
    involved_parties: help?.involvedParties?.INVOLVED_PARTIES_TAB?.[currentLangUpper],
    recipients: help?.recipients?.RECIPIENTS_TAB?.[currentLangUpper],
    translations: help?.translations?.TRANSLATIONS_TAB?.[currentLangUpper],
    summary: help?.summary?.SUMMARY_TAB?.[currentLangUpper],
    feedback: help?.feedback?.FEEDBACK_TAB?.[currentLangUpper],
    summary_feedback: help?.summaryFeedback?.SUMMARY_FEEDBACK_TAB?.[currentLangUpper],
    summary_plant_feedback: help?.summaryPlantFeedback?.SUMMARY_PLANT_FEEDBACK_TAB?.[currentLangUpper],
    plant_feedback: help?.plant_feedback?.PLANT_FEEDBACK_TAB?.[currentLangUpper],
  };

  const innerHTML = (data: string) => ({ __html: data });

  const getCurrentHelp = async () => {
    if (tab === 'plant-feedback') {
      const backPath = inSubCR ? 'SUMMARY_PLANT_FEEDBACK' : 'SUMMARY_FEEDBACK';

      try {
        const curHelp = await campaignServices.getHelps(backPath);
        setCurHelp({ ...help, [tab]: curHelp, focus: null });
      } catch (error) {
        handleCriticalError(error);
      }
    }

    if (!help?.[tab] || Object.keys(help?.[tab])?.length === 0) {
      try {
        const curHelp = await campaignServices.getHelps(tab.toUpperCase());
        setCurHelp({ ...help, [tab]: curHelp, focus: null });
      } catch (error) {
        handleCriticalError(error);
      }
    }
  };

  const openHelpInNewTab = () => {
    const newWindow = window.open();
    let page = `<html>
          <head>
            <title>${tab}_help</title>
          </head>
          <body>
            <h1 style="color:#007a94">${t('help.quickHelp').toUpperCase()}</h1>
            <p style="font-size: 20px; font-weight: bold">${tab.toUpperCase().replace('_', ' ')}</p>
            <p>${helpTab[tab]}</p>`;

    if ((area() && helpTab[tab]) !== '' && help[tab] !== undefined) {
      page += `<p>${help[tab]?.[`${tab.toUpperCase()}_${area()}`]?.[currentLangUpper]}</p>`;
    }

    page += '</body></html>';

    if (newWindow) newWindow.document.write(page);
  };

  const updateWidths = (value: number, save: boolean = false) => {
    setResizer(value, save);
    setWidth(value);
  };

  const saveResizeValue = (value: number) => {
    updateWidths(value, true);
  };

  useEffect(() => {
    getCurrentHelp();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setWidth(resizerWidth);
  }, [resizerWidth]);

  return (
    <div className={styles.Help} style={{ width: `${width}px` }}>
      <Resizer
        key={userData?.configuration?.sizeSidebarRight}
        position="Left"
        range={{ min: HELP_WIDTH.MIN, max: HELP_WIDTH.MAX }}
        tooltipText={t('resizeX', { x: t('help.quickHelp') })}
        onResize={setResizer}
        saveResizeValue={saveResizeValue}
      />
      <div id="help-title" className={styles.Title}>
        <CloseButton
          onClick={() =>
            setHelp({
              ...campaignHelp,
              show: !campaignHelp.show,
            })}
          closeStyle={styles.Close}
        />
        {t('help.quickHelp')}
        <i className={`di icon-link-extern ${styles.IconHelp}`} onClick={() => openHelpInNewTab()} />
      </div>
      <div className={`plain-html ${styles.HelpContent}`}>
        <div dangerouslySetInnerHTML={innerHTML(helpTab[tab])} />
        {(area() && helpTab[tab]) !== '' && help[tab] !== undefined && (
          <div>
            <p
              dangerouslySetInnerHTML={innerHTML(help?.[tab]?.[`${tab.toUpperCase()}_${area()}`]?.[currentLangUpper])}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userData: getUserAll(state),
  resizerWidth: getHelpResizer(state),
  campaignHelp: getCampaignHelp(state),
  help: getHelp(state),
});

const actionCreators = {
  setResizer: setResizerHelp,
  setCurHelp: setHelp,
  setHelp: setCampaignHelp,
};

export default connect(mapStateToProps, actionCreators)(React.memo(Help, (prev, next) => (
  JSON.stringify(prev) === JSON.stringify(next)
)));
